// Loxa Product Protection — custom integration for Pay Smart Home.
//
// This script works alongside the Loxa Shopify app embed. It does not change
// how the storefront looks — it keeps protection-plan line items in step
// with the products they cover.
//
// What it does:
//  1. Checkout label — adds a readable "Loxa Insurance" line-item property
//     at add-to-cart, so customers see their protection plan at checkout.
//  2. Quantity sync — when a protected product's cart quantity changes, the
//     matching protection line is updated in the same operation. Removing a
//     protected product also removes its protection line.
//  3. Safety check — after any cart update, product and protection
//     quantities are verified and corrected if they ever drift apart.
//
// Integration points:
//  - Wraps window.Shopify.changeItem (the theme's cart quantity API).
//  - Intercepts clicks on the cart remove control ([data-cartitem-remove]).
//  - Listens for the theme's `cartcount:update` event.
//  - Extends five methods on the Loxa app object (window.LoxaShopify).
//  - Narrowly wraps window.fetch to adjust the properties on Loxa's own
//    insurance-add request (identified by its X-Loxa-Separate-Add header).
//  - Makes its own cart requests via XMLHttpRequest so it never interferes
//    with other apps' fetch wrappers (and vice versa).
//
// Maintained by Loxa (loxacover.com). Please contact Loxa before editing or
// removing this file.

(function () {
  if (window.__loxaPshInstalled) return;
  window.__loxaPshInstalled = true;

  function log(msg, data) {
    var lx = window.LoxaShopify;
    if (lx && typeof lx.log === "function") lx.log("[loxa-psh] " + msg, data);
  }

  function logError(msg, err) {
    var lx = window.LoxaShopify;
    if (lx && typeof lx.error === "function")
      lx.error("[loxa-psh] " + msg, err);
    else console.error("[loxa-psh] " + msg, err);
  }

  // ─── XHR cart helpers ─────────────────────────────────────────────────

  function cartRequest(method, path, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(method, path, true);
      xhr.setRequestHeader("Accept", "application/json");
      if (body) xhr.setRequestHeader("Content-Type", "application/json");
      xhr.withCredentials = true;
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            reject(e);
          }
        } else {
          reject(new Error(method + " " + path + " HTTP " + xhr.status));
        }
      };
      xhr.onerror = function () {
        reject(new Error(method + " " + path + " network error"));
      };
      xhr.send(body ? JSON.stringify(body) : null);
    });
  }

  function getCart() {
    return cartRequest("GET", "/cart.js", null);
  }

  function updateCart(updates) {
    return cartRequest("POST", "/cart/update.js", { updates: updates });
  }

  // ─── Line item lookups ────────────────────────────────────────────────

  function isInsuranceLine(item) {
    var p = (item && item.properties) || {};
    return !!(p._loxaInsuranceCodeSep || p._insuredProductId);
  }

  function linkIdOf(item) {
    var p = (item && item.properties) || {};
    return p._loxaLinkId ? String(p._loxaLinkId) : null;
  }

  function itemByKey(cart, key) {
    if (!cart || !cart.items || !key) return null;
    var k = String(key);
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].key === k) return cart.items[i];
    }
    return null;
  }

  function findInsuranceFor(cart, productItem) {
    var linkId = linkIdOf(productItem);
    if (!linkId || !cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (isInsuranceLine(item) && linkIdOf(item) === linkId) return item;
    }
    return null;
  }

  function findProductFor(cart, insuranceItem) {
    var linkId = linkIdOf(insuranceItem);
    if (!linkId || !cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!isInsuranceLine(item) && linkIdOf(item) === linkId) return item;
    }
    return null;
  }

  // Line keys are `<variantId>:<hash>`; the hash changes whenever a line's
  // properties are rewritten (e.g. core clearing _loxaLinkId during its
  // "quiet" insurance removal). Empire's DOM keeps the old key after those
  // quiet updates, and sending a stale key to /cart/change.js is a
  // guaranteed 400. Recover via the variant id embedded in the key — but
  // only when it resolves to exactly one line.
  function resolveItemByKey(cart, key) {
    var item = itemByKey(cart, key);
    if (item) return item;
    var vid = String(key || "").split(":")[0];
    if (!vid || !cart || !cart.items) return null;
    var matches = cart.items.filter(function (it) {
      return String(it.id) === vid;
    });
    if (matches.length === 1) {
      log("stale line key recovered via variant id", {
        from: key,
        to: matches[0].key,
      });
      return matches[0];
    }
    return null;
  }

  // ─── Unit 2: cart qty sync via Shopify.changeItem ─────────────────────

  // After the customer removes just the protection line, blank the product's
  // `_loxaLinkId` (core's convention for "explicitly unpaired" — it skips
  // re-pairing when the property exists but is empty). /cart/change.js
  // replaces properties wholesale, so merge the existing set.
  function clearLinkId(product) {
    var merged = {};
    var props = product.properties || {};
    for (var k in props) {
      if (Object.prototype.hasOwnProperty.call(props, k)) merged[k] = props[k];
    }
    merged._loxaLinkId = "";
    return cartRequest("POST", "/cart/change.js", {
      id: product.key,
      quantity: product.quantity,
      properties: merged,
    }).catch(function (err) {
      logError("failed to clear _loxaLinkId after protection removal", err);
    });
  }

  function installChangeItemWrap() {
    var sh = window.Shopify;
    if (!sh || typeof sh.changeItem !== "function") return false;
    if (sh.__loxaPshChangeItemWrapped) return true;
    sh.__loxaPshChangeItemWrapped = true;

    var orig = sh.changeItem;

    // Mirror api.jquery's contract: success calls the callback when given,
    // otherwise Shopify.onCartUpdate. /cart/update.js returns the same cart
    // JSON shape as /cart/change.js, so Empire's render path is unaffected.
    function finish(cart, callback) {
      if (typeof callback === "function") callback(cart);
      else if (typeof sh.onCartUpdate === "function") sh.onCartUpdate(cart);
    }

    sh.changeItem = function (key, quantity, callback) {
      var qty = parseInt(quantity, 10);
      if (!isFinite(qty) || qty < 0) qty = 0;

      getCart()
        .then(function (cart) {
          var item = resolveItemByKey(cart, key);
          // Unresolvable line — nothing better than the theme's own call.
          if (!item) return orig.call(sh, key, quantity, callback);
          // From here on, always use item.key (the CURRENT key) — the key
          // Empire passed may be stale, and /cart/change.js 400s on it.

          if (isInsuranceLine(item)) {
            var product = findProductFor(cart, item);
            if (qty === 0) {
              // Customer removed just the protection. Unpair the product
              // FIRST — it rewrites the product's properties and therefore
              // its key — so Empire's render after the removal picks up the
              // fresh keys instead of going stale.
              var unpair = product ? clearLinkId(product) : Promise.resolve();
              return unpair.then(function () {
                return orig.call(sh, item.key, 0, callback);
              });
            }
            if (!product) return orig.call(sh, item.key, quantity, callback);
            // Direct qty edits on the insurance line snap back to the
            // product's qty — the product line is the source of truth.
            var snap = {};
            snap[item.key] = product.quantity;
            return updateCart(snap)
              .then(function (updatedCart) {
                log(
                  "insurance qty snapped back to product qty " +
                    product.quantity,
                );
                finish(updatedCart, callback);
              })
              .catch(function (err) {
                logError("insurance snap-back failed, passing through", err);
                orig.call(sh, item.key, quantity, callback);
              });
          }

          var insurance = findInsuranceFor(cart, item);
          if (!insurance) return orig.call(sh, item.key, quantity, callback);

          var updates = {};
          updates[item.key] = qty;
          updates[insurance.key] = qty;
          return updateCart(updates)
            .then(function (updatedCart) {
              log("synced qty " + qty + " across product + insurance", {
                product: item.key,
                insurance: insurance.key,
              });
              finish(updatedCart, callback);
            })
            .catch(function (err) {
              logError(
                "atomic qty sync failed, falling back to single-line change",
                err,
              );
              orig.call(sh, item.key, quantity, callback);
            });
        })
        .catch(function (err) {
          logError("cart fetch failed, passing through", err);
          orig.call(sh, key, quantity, callback);
        });
    };

    log("Shopify.changeItem wrap installed");
    return true;
  }

  // ─── Unit 2b: remove-link (trash icon) interception ───────────────────

  // Empire's trash control (`[data-cartitem-remove]`) does not route
  // through Shopify.changeItem (observed: product removed, insurance line
  // orphaned), so intercept the click itself in the capture phase, perform
  // the removal of the line plus its paired insurance atomically, then
  // reload the cart page. Quantity-stepper changes still flow through the
  // changeItem wrap above.
  function lineFromHref(href, cart) {
    var m = /[?&]line=(\d+)/.exec(href || "");
    if (!m || !cart.items) return null;
    var idx = parseInt(m[1], 10);
    return cart.items[idx - 1] || null;
  }

  document.addEventListener(
    "click",
    function (evt) {
      var link =
        evt.target && evt.target.closest
          ? evt.target.closest("[data-cartitem-remove]")
          : null;
      if (!link) return;
      evt.preventDefault();
      evt.stopPropagation();

      var href = link.getAttribute("href") || "/cart";

      getCart()
        .then(function (cart) {
          var row = link.closest("[data-cartitem-key]");
          var item = row
            ? resolveItemByKey(cart, row.getAttribute("data-cartitem-key"))
            : null;
          if (!item) item = lineFromHref(href, cart);
          if (!item) {
            // Can't resolve the line — fall back to the link's own removal.
            window.location.href = href;
            return false;
          }

          var updates = {};
          updates[item.key] = 0;
          var unpairAfter = null;
          if (isInsuranceLine(item)) {
            // Trash on the insurance row itself: opt-out, unpair the product.
            unpairAfter = findProductFor(cart, item);
          } else {
            var insurance = findInsuranceFor(cart, item);
            if (insurance) updates[insurance.key] = 0;
          }

          log("remove-link intercepted, removing", updates);
          return updateCart(updates)
            .then(function () {
              return unpairAfter ? clearLinkId(unpairAfter) : null;
            })
            .then(function () {
              return true;
            });
        })
        .then(function (shouldReload) {
          if (shouldReload) window.location.reload();
        })
        .catch(function (err) {
          logError("remove-link interception failed, following link", err);
          window.location.href = href;
        });
    },
    true,
  );

  // ─── Unit 2c: cart cosmetics — insured row totals + opt-out banner ────

  // Empire's row total shows the product line only; the protection cost
  // lives on the hidden separate insurance line. Rewrite the row total to
  // product + insurance. Also render a dark-grey banner on inclusive lines
  // the customer opted out of (core renders nothing for those, and its
  // cleanup strips `.loxa-insurance-block` from uninsured rows — hence the
  // separate `loxa-optout-block` class and own CSS).
  // Pay Smart Home brand: body/button slate #4e5151, links #01448a.
  var OPTOUT_CSS =
    // Loxa offer cards sit borderless like the rest of the site's blocks.
    ".loxa-card{border:none !important;}" +
    // Match the live product's Loxa styling: .loxa-container is #F5F5F5,
    // 8px radius; .loxa-container.opted-out flips to #999999.
    // Cart: injected opt-out banner.
    ".loxa-optout-block{display:flex;align-items:center;gap:8px;" +
    "margin-top:8px;padding:8px 10px;background:#999999;border-radius:8px;}" +
    ".loxa-optout-block img{filter:grayscale(1);opacity:.7;display:block;}" +
    ".loxa-optout-block .loxa-optout-detail{font-size:14px;}" +
    // PDP: core's inclusive card gets .loxa-opted-out when the customer
    // opts out.
    ".loxa-card.loxa-inclusive-badge.loxa-opted-out{" +
    "background:#999999 !important;}" +
    ".loxa-card.loxa-inclusive-badge.loxa-opted-out .loxa-add-on-logo{" +
    "filter:grayscale(1);opacity:.7;}" +
    // Description and re-add link take the title's colour (inherit) so the
    // whole card stays uniformly readable on the #999999 background; the
    // link keeps underline + weight to stand apart.
    ".loxa-card.loxa-inclusive-badge.loxa-opted-out .loxa-inclusive-description{" +
    "color:inherit !important;}" +
    ".loxa-card.loxa-inclusive-badge.loxa-opted-out .loxa-inclusive-readd{" +
    "color:inherit !important;opacity:1;text-decoration:underline;" +
    "font-weight:600;}" +
    // Mobile: core sizes the sidebar 100vh, which extends behind the
    // browser's collapsing toolbar — the bottom (CTA/legal) can never be
    // scrolled into view. dvh tracks the VISIBLE viewport; contain stops
    // edge-scrolls dragging the page behind; safe-area clears the iOS
    // home indicator.
    // !important throughout: core's `all:unset` reset beats plain
    // declarations on the sidebar (it also flips box-sizing to content-box,
    // hence the explicit border-box). dvh keeps the bottom reachable when
    // the mobile browser toolbar is expanded; the content padding lets the
    // user scroll 50px past the last content. Verified live at 390px.
    "@media (max-width:768px){#loxa-shopify-sidebar{" +
    "box-sizing:border-box !important;" +
    "height:100dvh !important;" +
    "overscroll-behavior:contain !important;" +
    "-webkit-overflow-scrolling:touch;}}" +
    "#loxa-shopify-sidebar-content{padding-bottom:75px !important;}";

  function ensureOptoutStyles() {
    if (document.getElementById("loxa-psh-optout-css")) return;
    var styleEl = document.createElement("style");
    styleEl.id = "loxa-psh-optout-css";
    styleEl.textContent = OPTOUT_CSS;
    (document.head || document.documentElement).appendChild(styleEl);
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return "£" + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function termFromCode(code) {
    var m = /^[A-Za-z]*?(\d+)/.exec(String(code || ""));
    return m ? m[1] : "";
  }

  function rowForItem(item) {
    return (
      document.querySelector('[data-cartitem-key="' + item.key + '"]') ||
      // After a property rewrite (e.g. unpairing on insurance removal) the
      // line key changes while the DOM row keeps the old one — fall back to
      // the variant id Empire stamps on each row.
      document.querySelector('[data-cartitem-id="' + item.id + '"]')
    );
  }

  // Always computes — combined when insured, product-only when not — so the
  // row self-corrects when the insurance is removed on its own. Writes only
  // on change, so the pass settles under the cart MutationObserver below.
  function syncRowTotal(cart, item) {
    var row = rowForItem(item);
    var totalEl = row && row.querySelector("[data-cartitem-total]");
    if (!totalEl) return;
    var insurance = findInsuranceFor(cart, item);
    var formatted = formatMoney(
      item.final_line_price + (insurance ? insurance.final_line_price : 0),
    );
    if (totalEl.textContent.trim() !== formatted) {
      totalEl.textContent = formatted;
    }
  }

  function injectOptoutBanner(item) {
    var row = rowForItem(item);
    if (!row || row.querySelector(".loxa-optout-block")) return;
    ensureOptoutStyles();

    var term = termFromCode(item.properties._loxaOptedOut);
    var logoUrl =
      (window.loxaShopifySettings && window.loxaShopifySettings.logoUrl) || "";

    var banner = document.createElement("div");
    banner.className = "loxa-optout-block";
    banner.setAttribute("data-loxa-optout-key", item.key);
    var html = "";
    if (logoUrl) {
      html += '<img src="' + logoUrl + '" alt="Loxa" width="28" height="28">';
    }
    html +=
      '<div class="loxa-optout-detail">' +
      (term
        ? "Free " + term + " Year Insurance Opted Out"
        : "Free Insurance Opted Out") +
      "</div>";
    banner.innerHTML = html;

    var title = row.querySelector(".cart-item--content-title");
    if (title && title.parentNode) {
      title.parentNode.insertBefore(banner, title.nextSibling);
    } else {
      row.appendChild(banner);
    }
  }

  var cosmeticsTimer = null;

  function runCartCosmetics() {
    getCart()
      .then(function (cart) {
        var keysInCart = {};
        var visibleCount = 0;
        (cart.items || []).forEach(function (item) {
          keysInCart[item.key] = true;
          var props = item.properties || {};
          if (isInsuranceLine(item)) return;
          visibleCount += item.quantity;
          syncRowTotal(cart, item);
          if (props._loxaOptedOut && !props._loxaInclusiveCodeSep) {
            injectOptoutBanner(item);
          }
        });
        // Header badge uses Shopify's item_count, which includes the hidden
        // insurance units — show the count of visible items instead.
        document
          .querySelectorAll("[data-header-cart-count]")
          .forEach(function (el) {
            var countStr = String(visibleCount);
            if (el.getAttribute("data-header-cart-count") !== countStr) {
              el.setAttribute("data-header-cart-count", countStr);
            }
            var text = el.textContent.trim();
            if (text !== "" && text !== countStr) {
              el.textContent = countStr;
            }
          });
        // Subtotals (header cart-title + cart footer). Empire refreshes
        // these only on its own cart responses; core's quiet insurance
        // removal bypasses them — write the live figure.
        var subtotal = formatMoney(cart.total_price);
        document.querySelectorAll("[data-cart-total]").forEach(function (el) {
          if (el.textContent.trim() !== subtotal) el.textContent = subtotal;
        });
        // Drop opt-out banners for lines no longer in the cart.
        document.querySelectorAll(".loxa-optout-block").forEach(function (el) {
          if (!keysInCart[el.getAttribute("data-loxa-optout-key")]) el.remove();
        });
      })
      .catch(function (err) {
        logError("cart cosmetics pass failed", err);
      });
  }

  function scheduleCartCosmetics() {
    if (cosmeticsTimer) clearTimeout(cosmeticsTimer);
    cosmeticsTimer = setTimeout(runCartCosmetics, 400);
  }

  // Re-apply after core finishes its banner pass, after the banner's
  // "Remove Insurance" flow completes, and after Empire re-renders.
  document.addEventListener("loxa:after:processCart", scheduleCartCosmetics);
  document.addEventListener("loxa:after:removeInsurance", scheduleCartCosmetics);

  // Empire's morphdom render can land AFTER the debounced pass and reset
  // row totals to the theme's product-only price. Watch the cart DOM and
  // re-apply on every render; the change-only writes above stop our own
  // pass from re-triggering the observer indefinitely.
  var cosmeticsObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var target = mutations[i].target;
      var el = target.nodeType === 1 ? target : target.parentElement;
      if (
        el &&
        el.closest &&
        el.closest("[data-cart-item-list], [data-cart-sidebar], .cart-item")
      ) {
        scheduleCartCosmetics();
        return;
      }
    }
  });

  // ─── Unit 3: reconcile safety net on cartcount:update ─────────────────

  // Only fixes qty mismatches on matched pairs. Deliberately does NOT remove
  // orphan insurance lines: during an add, core creates the insurance line
  // moments before/after the product line, and an orphan sweep could race it.
  var reconcileTimer = null;
  var reconcileBusy = false;

  function runReconcile() {
    if (reconcileBusy) return;
    reconcileBusy = true;
    getCart()
      .then(function (cart) {
        var fixes = {};
        var found = false;
        (cart.items || []).forEach(function (item) {
          if (isInsuranceLine(item)) return;
          var insurance = findInsuranceFor(cart, item);
          if (insurance && insurance.quantity !== item.quantity) {
            fixes[insurance.key] = item.quantity;
            found = true;
          }
        });
        if (!found) return null;
        log("reconciling insurance quantities", fixes);
        return updateCart(fixes);
      })
      .catch(function (err) {
        logError("reconcile pass failed", err);
      })
      .then(function () {
        reconcileBusy = false;
      });
  }

  window.addEventListener("cartcount:update", function () {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(runReconcile, 600);
    scheduleCartCosmetics();
  });

  // ─── Unit 1: checkout label (unchanged from loxa-checkout-label.js) ───
  //
  // Core's separate-mode flow attaches only underscore-prefixed properties
  // which Shopify hides at checkout. Wrapping _injectInsuranceProperties
  // injects an additional visible `Loxa Insurance` property into the
  // cart-add body, catching every code path that writes to it (the form
  // listener never fires in separate mode).

  var LABEL_KEY = "Loxa Insurance";

  function labelValueFor(plan) {
    if (!plan) return null;
    var premium =
      typeof plan.insurance_price === "number"
        ? plan.insurance_price
        : parseFloat(plan.insurance_price || "0");
    if (!isNaN(premium) && premium === 0) return "Included";
    return plan.name || "Product Protection";
  }

  function appendLabelToBody(args, value) {
    if (!args || !args[1] || !value) return args;
    var options = args[1];
    var body = options.body;

    if (typeof body === "string") {
      try {
        var parsed = JSON.parse(body);
        if (
          parsed.items &&
          Array.isArray(parsed.items) &&
          parsed.items.length > 0
        ) {
          if (!parsed.items[0].properties) parsed.items[0].properties = {};
          parsed.items[0].properties[LABEL_KEY] = value;
        } else {
          if (!parsed.properties) parsed.properties = {};
          parsed.properties[LABEL_KEY] = value;
        }
        options.body = JSON.stringify(parsed);
      } catch (e) {
        options.body =
          body +
          "&properties%5B" +
          encodeURIComponent(LABEL_KEY) +
          "%5D=" +
          encodeURIComponent(value);
      }
      return args;
    }

    if (typeof FormData !== "undefined" && body instanceof FormData) {
      body.append("properties[" + LABEL_KEY + "]", value);
      return args;
    }

    if (
      typeof URLSearchParams !== "undefined" &&
      body instanceof URLSearchParams
    ) {
      body.append("properties[" + LABEL_KEY + "]", value);
      return args;
    }

    return args;
  }

  // Reshape the attached inclusive properties core just injected:
  //   _loxaInsuranceCodeSep → _loxaInclusiveCodeSep  (the back-office reads
  //     the inclusive code from its own key — _loxaInsuranceCode(Sep) means
  //     a paid plan)
  //   _loxaInsuranceName    → dropped (redundant; nothing renders it)
  //   _insuredProduct       → added (product title; the order-processing
  //     script flags it as a missing property otherwise)
  function reshapeInclusiveProps(args) {
    if (!args || !args[1]) return args;
    var lx = window.LoxaShopify;
    var ctx =
      (lx &&
        (lx.lastProductContext ||
          (typeof lx.getProductContext === "function" &&
            lx.getProductContext()))) ||
      null;
    var insuredTitle = (ctx && ctx.productTitle) || "";
    var options = args[1];
    var body = options.body;

    function reshape(props) {
      if (!props || !props._loxaInsuranceCodeSep) return;
      props._loxaInclusiveCodeSep = props._loxaInsuranceCodeSep;
      delete props._loxaInsuranceCodeSep;
      delete props._loxaInsuranceName;
      if (insuredTitle) props._insuredProduct = insuredTitle;
      // Policy processing requires _sumInsured and Premium/_Premium and
      // skips the line without them. (_loxaPremium stays — the cart banner
      // reads it. _insuredProductId is deliberately NOT set here: combined
      // with an insurance code it makes core hide the product row; the
      // processing script derives it from the line's own variant instead.)
      if (props._loxaSumInsured) {
        props._sumInsured = props._loxaSumInsured;
        delete props._loxaSumInsured;
      }
      props._Premium = props._loxaPremium || "0.00";
      var category =
        lx && lx.lastInsuranceData && lx.lastInsuranceData.insurance_category;
      if (category) props._Category = category;
      props._Source = "online";
    }

    if (typeof body === "string") {
      try {
        var parsed = JSON.parse(body);
        if (parsed.items && Array.isArray(parsed.items) && parsed.items.length > 0) {
          reshape(parsed.items[0].properties);
        } else {
          reshape(parsed.properties);
        }
        options.body = JSON.stringify(parsed);
      } catch (e) {
        // URL-encoded string fallback (mirrors core's own fallback shape)
        var premiumMatch = /properties%5B_loxaPremium%5D=([^&]*)/.exec(body);
        options.body = body
          .replace(
            "properties%5B_loxaInsuranceCodeSep%5D",
            "properties%5B_loxaInclusiveCodeSep%5D",
          )
          .replace(
            "properties%5B_loxaSumInsured%5D",
            "properties%5B_sumInsured%5D",
          )
          .replace(/&properties%5B_loxaInsuranceName%5D=[^&]*/, "");
        if (insuredTitle) {
          options.body +=
            "&properties%5B_insuredProduct%5D=" +
            encodeURIComponent(insuredTitle);
        }
        options.body +=
          "&properties%5B_Premium%5D=" +
          (premiumMatch ? premiumMatch[1] : "0.00");
        var fallbackCategory =
          lx && lx.lastInsuranceData && lx.lastInsuranceData.insurance_category;
        if (fallbackCategory) {
          options.body +=
            "&properties%5B_Category%5D=" + encodeURIComponent(fallbackCategory);
        }
        options.body += "&properties%5B_Source%5D=online";
      }
      return args;
    }

    if (
      (typeof FormData !== "undefined" && body instanceof FormData) ||
      (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams)
    ) {
      var code = body.get("properties[_loxaInsuranceCodeSep]");
      if (code != null) {
        body.delete("properties[_loxaInsuranceCodeSep]");
        body.delete("properties[_loxaInsuranceName]");
        body.append("properties[_loxaInclusiveCodeSep]", code);
        if (insuredTitle) body.append("properties[_insuredProduct]", insuredTitle);
        var sumInsured = body.get("properties[_loxaSumInsured]");
        if (sumInsured != null) {
          body.delete("properties[_loxaSumInsured]");
          body.append("properties[_sumInsured]", sumInsured);
        }
        var premium = body.get("properties[_loxaPremium]");
        body.append("properties[_Premium]", premium != null ? premium : "0.00");
        var category =
          lx && lx.lastInsuranceData && lx.lastInsuranceData.insurance_category;
        if (category) body.append("properties[_Category]", category);
        body.append("properties[_Source]", "online");
      }
    }

    return args;
  }

  function patchLabel(lx) {
    if (
      typeof lx._injectInsuranceProperties !== "function" ||
      lx.__pshLabelPatched
    )
      return;
    lx.__pshLabelPatched = true;

    var orig = lx._injectInsuranceProperties;
    lx._injectInsuranceProperties = function (args, plan) {
      var result = orig.call(this, args, plan);
      reshapeInclusiveProps(result);
      var value = labelValueFor(plan);
      if (value) appendLabelToBody(result, value);
      return result;
    };
  }

  // ─── Unit 1b: separate insurance line property polish ─────────────────

  // Core adds the paid (add-on) insurance line itself via fetch, marked with
  // an X-Loxa-Separate-Add header. Narrowly wrap fetch to adjust that one
  // request's properties at source:
  //   _insuredProduct → "Insured Product"  (visible at checkout — tells the
  //     customer which product the protection covers; the order-processing
  //     script reads both keys)
  //   _Source: "online"                    (order-source metadata)
  // Everything else passes through untouched; failures never block the add.
  function installSeparateAddShim() {
    if (window.__loxaPshSeparateAddShim) return;
    window.__loxaPshSeparateAddShim = true;

    var origFetch = window.fetch;
    if (typeof origFetch !== "function") return;

    window.fetch = function (input, init) {
      try {
        var opts = init || (input && typeof input === "object" ? input : null);
        var headers = opts && opts.headers;
        var marked =
          headers &&
          (typeof headers.get === "function"
            ? headers.get("X-Loxa-Separate-Add")
            : headers["X-Loxa-Separate-Add"]);
        if (marked && opts.body && typeof opts.body === "string") {
          var payload = JSON.parse(opts.body);
          var item = payload.items && payload.items[0];
          if (item && item.properties) {
            if (item.properties._insuredProduct) {
              item.properties["Insured Product"] = item.properties._insuredProduct;
              delete item.properties._insuredProduct;
            }
            item.properties._Source = "online";
            opts.body = JSON.stringify(payload);
          }
        }
      } catch (e) {
        /* never block the add */
      }
      return origFetch.apply(this, arguments);
    };
  }

  // ─── Unit 1c: product-first add order ─────────────────────────────────

  // Core's default separate flow adds the product first, insurance second —
  // and Shopify lists newest lines first, so the insurance row lands ABOVE
  // its product in cart and checkout. Flip the order (insurance first,
  // product second) so the product renders above the protection that covers
  // it. Falls back to core's default order if the insurance-first add fails.
  function patchAddOrder(lx) {
    if (
      typeof lx._handleCartAddIntercept !== "function" ||
      lx.__pshAddOrderPatched
    )
      return;
    lx.__pshAddOrderPatched = true;

    var origHandleCartAdd = lx._handleCartAddIntercept.bind(lx);
    lx._handleCartAddIntercept = function (originalFetch, ctx, args) {
      var parsedBody = lx._parseRequestBody(args[1] || {});

      var selected = lx.getSelectedInsurance();
      var paidPlan = null;
      if (selected) {
        var selPrice =
          typeof selected.insurance_price === "number"
            ? selected.insurance_price
            : parseFloat(selected.insurance_price || "0");
        if (selPrice > 0) paidPlan = selected;
      }
      // Inclusive/none: core's default handles it (attached props, no
      // separate line, no ordering concern).
      if (!paidPlan) return origHandleCartAdd(originalFetch, ctx, args);

      if (parsedBody && parsedBody.sections) {
        lx._cachedSections = parsedBody.sections;
        if (parsedBody.sections_url) lx._cachedSectionsUrl = parsedBody.sections_url;
      }

      lx._dispatchHook("loxa:before:cartAdd", { args: args, parsedBody: parsedBody });

      var linkId = lx._generateLinkId();
      var productVariantId = lx._extractVariantIdFromArgs(args);

      return lx
        ._resolveInsuranceVariantId(paidPlan)
        .then(function (resolvedId) {
          return lx._addInsuranceLineItem(paidPlan, null, resolvedId, null, linkId);
        })
        .then(function (insResponse) {
          if (!insResponse || !insResponse.ok) {
            logError("insurance-first add failed; using default order", null);
            return origHandleCartAdd(originalFetch, ctx, args);
          }
          var modifiedArgs = lx._injectLinkId(args, linkId);
          return originalFetch.apply(ctx, modifiedArgs).then(function (productResponse) {
            if (!productResponse.ok) return productResponse;
            lx._showSkeletonsForInsuredProducts();
            if (productVariantId) {
              setTimeout(function () {
                lx._linkProductToInsurance(productVariantId, linkId);
              }, 2000);
            }
            return lx._mergeSectionsIntoResponse(productResponse, insResponse);
          });
        })
        .then(function (finalResponse) {
          lx._dispatchHook("loxa:after:cartAdd", {
            route: "psh-product-first",
            response: finalResponse,
          });
          return finalResponse;
        })
        .catch(function (err) {
          logError("product-first add override threw, passing through", err);
          return originalFetch.apply(ctx, args);
        });
    };
  }

  // Core classifies cart lines via _insCode (_loxaInsuranceCodeSep ||
  // _loxaInsuranceCode). After the reshape above, inclusive products carry
  // _loxaInclusiveCodeSep instead, so extend the classifier or the attached
  // cart banner ("X Year Protection Included") stops rendering. Insurance-
  // LINE classification in core always pairs _insCode with _insuredProductId,
  // which attached products never have — so this only re-enables the banner.
  function patchInsCode(lx) {
    if (typeof lx._insCode !== "function" || lx.__pshInsCodePatched) return;
    lx.__pshInsCodePatched = true;

    var orig = lx._insCode.bind(lx);
    lx._insCode = function (props) {
      return (props && props._loxaInclusiveCodeSep) || orig(props);
    };
  }

  // ─── Unit 4: deterministic _loxaLinkId ────────────────────────────────

  function patchLinkId(lx) {
    if (typeof lx._generateLinkId !== "function" || lx.__pshLinkIdPatched)
      return;
    lx.__pshLinkIdPatched = true;

    var orig = lx._generateLinkId.bind(lx);
    lx._generateLinkId = function () {
      try {
        var ctx =
          typeof lx.getProductContext === "function"
            ? lx.getProductContext()
            : null;
        var variantId = ctx && ctx.variantId ? String(ctx.variantId) : null;
        var code =
          lx.selectedInsuranceCode && lx.selectedInsuranceCode !== "__none"
            ? String(lx.selectedInsuranceCode)
            : null;
        if (variantId && code) return "lx_" + variantId + "_" + code;
      } catch (e) {
        /* fall through to core's random id */
      }
      return orig();
    };
  }

  // ─── Unit 5: "Included" suffix on free-protection cart banners ────────

  // The cart banner (.loxa-insurance-block → .loxa-detail) renders
  // "{term} Year Protection" for free inclusive plans. Rewrite it to
  // "Free {term} Year Insurance Included". Paid banners
  // ("{term} Year Protection: £x.xx") are untouched.
  function patchBanner(lx) {
    if (typeof lx._injectInsuranceBanner !== "function" || lx.__pshBannerPatched)
      return;
    lx.__pshBannerPatched = true;

    var orig = lx._injectInsuranceBanner;
    lx._injectInsuranceBanner = function (cartItemEl, cartItem, isAttached) {
      var result = orig.call(this, cartItemEl, cartItem, isAttached);
      try {
        var props = (cartItem && cartItem.properties) || {};
        var isFree = isAttached
          ? (props._loxaPremium || "0.00") === "0.00"
          : !props.Premium || props.Premium === "£0.00";
        if (isFree && cartItemEl) {
          var detail = cartItemEl.querySelector(
            '.loxa-insurance-block[data-loxa-insurance-key="' +
              cartItem.key +
              '"] .loxa-detail',
          );
          if (detail) {
            var term = props._loxaTerm ? String(props._loxaTerm) : "";
            detail.textContent = term
              ? "Free " + term + " Year Insurance Included"
              : "Free Insurance Included";
          }
        }
      } catch (e) {
        /* cosmetic only — never block the banner render */
      }
      return result;
    };
  }

  // ─── Unit 6: add-on offer above the add-to-basket button ──────────────

  // The PDP offers mount into theme app-block anchors. Move the add-on
  // anchor (offer card included) above the buy row — `.product-form--atc`
  // wraps both the quantity selector and the add-to-basket button, so the
  // card sits above the quantity buttons too. The inclusive anchor stays
  // where the theme editor placed it.
  function repositionAddonAnchor() {
    var anchor = document.getElementById("loxa-shopify-addon-anchor");
    if (!anchor) return false;

    var buyRow = document.querySelector(".product-form--atc");
    if (!buyRow || !buyRow.parentNode) return false;
    if (anchor.nextElementSibling === buyRow) return true; // already in place
    buyRow.parentNode.insertBefore(anchor, buyRow);
    log("add-on anchor moved above the buy row");
    return true;
  }

  installSeparateAddShim();

  // ─── Boot ─────────────────────────────────────────────────────────────

  // Both LoxaShopify (async app embed) and Shopify.changeItem (Empire's
  // jQuery API) load at unpredictable times relative to this script. Poll
  // for up to ~10s, installing each piece as it appears.
  function tryInstallAll() {
    var lx = window.LoxaShopify;
    if (lx) {
      patchLabel(lx);
      patchLinkId(lx);
      patchBanner(lx);
      patchInsCode(lx);
      patchAddOrder(lx);
    }
    var changeItemDone = installChangeItemWrap();
    return !!(
      lx &&
      lx.__pshLabelPatched &&
      lx.__pshLinkIdPatched &&
      lx.__pshBannerPatched &&
      lx.__pshInsCodePatched &&
      lx.__pshAddOrderPatched &&
      changeItemDone
    );
  }

  if (!tryInstallAll()) {
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      if (tryInstallAll() || attempts >= 100) {
        clearInterval(iv);
        if (attempts >= 100) {
          logError(
            "gave up waiting for LoxaShopify / Shopify.changeItem after 10s",
            null,
          );
        }
      }
    }, 100);
  }

  // PDP anchor move + initial cart cosmetics. The anchor and the ATC button
  // exist at parse time on the PDP; retry briefly in case the section
  // hydrates late. Cosmetics re-run via the loxa/Empire event listeners.
  function bootDomWork() {
    ensureOptoutStyles();
    cosmeticsObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    var anchorAttempts = 0;
    var anchorTimer = setInterval(function () {
      anchorAttempts++;
      if (repositionAddonAnchor() || anchorAttempts >= 20) {
        clearInterval(anchorTimer);
      }
    }, 250);
    scheduleCartCosmetics();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootDomWork);
  } else {
    bootDomWork();
  }
})();
